import {
  ArrowDownRight,
  ArrowUpRight,
  DollarSign,
  SlidersHorizontal,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrencyRounded } from "@/lib/utils";

interface Props {
  totalCount: number;
  plusCount: number;
  minusCount: number;
  totalImpactValue: number;
}

export function StockAdjustmentSummaryCards({
  totalCount,
  plusCount,
  minusCount,
  totalImpactValue,
}: Props) {
  const roundedImpact = Math.round(totalImpactValue);
  const isNegative = roundedImpact < 0;
  const isPositive = roundedImpact > 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Total Penyesuaian
          </CardTitle>
          <SlidersHorizontal className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-muted-foreground" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold">{totalCount}</div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Koreksi langsung saldo fisik
          </p>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Penyesuaian Tambah (+)
          </CardTitle>
          <ArrowUpRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-emerald-500" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            {plusCount}
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Barang ditemukan / surplus
          </p>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Penyesuaian Kurang (-)
          </CardTitle>
          <ArrowDownRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-rose-500" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold text-rose-600 dark:text-rose-400">
            {minusCount}
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Tumpah, rusak, expired, hilang
          </p>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Nilai Dampak Finansial
          </CardTitle>
          <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-muted-foreground" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div
            className={`text-lg sm:text-2xl font-bold truncate ${
              isNegative
                ? "text-rose-600 dark:text-rose-400"
                : isPositive
                  ? "text-emerald-600 dark:text-emerald-400"
                  : ""
            }`}
          >
            {formatCurrencyRounded(totalImpactValue)}
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Estimasi selisih nilai aset
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
